import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Check, LayoutDashboard, LogIn, LogOut, Pencil, ScrollText, TerminalSquare, User, X } from "lucide-react";
import { toast } from "sonner";

function ProfileMenu({ user, onClose }) {
  const { logout, updateName } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [saving, setSaving] = useState(false);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    if (trimmed === user.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await updateName(trimmed);
      toast.success("DISPLAY NAME UPDATED");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      ref={menuRef}
      data-testid="profile-menu"
      className="absolute right-0 top-full mt-1 z-50 panel p-3 w-[260px] shadow-[6px_6px_0_0_rgba(0,0,0,1)]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2 pb-2 mb-2 border-b-2 border-dashed border-[color:var(--border-hard)]">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 border-2 border-[color:var(--border-hard)]" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-8 h-8 border-2 border-[color:var(--border-hard)] flex items-center justify-center bg-[color:var(--bg-panel)]">
            <User size={16} className="text-[color:var(--accent-cyan)]" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-black uppercase tracking-tight truncate">{user.name || "UNNAMED PUNK"}</div>
          <div className="font-mono text-[10px] text-[color:var(--text-muted)] truncate">{user.email}</div>
        </div>
      </div>

      <div className="text-[9px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
        DISPLAY NAME
      </div>
      {editing ? (
        <div className="flex items-stretch gap-1 mb-2">
          <input
            ref={inputRef}
            data-testid="profile-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setEditing(false); setName(user.name || ""); } }}
            maxLength={60}
            className="cy-input flex-1 text-xs py-1"
          />
          <button
            data-testid="profile-name-save"
            onClick={save}
            disabled={saving}
            className="cy-btn cy-btn-solid-yellow text-xs px-2"
            aria-label="Save name"
          >
            <Check size={14} />
          </button>
          <button
            data-testid="profile-name-cancel"
            onClick={() => { setEditing(false); setName(user.name || ""); }}
            className="cy-btn text-xs px-2"
            aria-label="Cancel"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          data-testid="profile-edit-name-btn"
          onClick={() => setEditing(true)}
          className="cy-btn text-xs w-full justify-between mb-2"
        >
          <span className="truncate">{user.name || "SET A NAME"}</span>
          <Pencil size={12} className="ml-2 shrink-0" />
        </button>
      )}

      <button
        data-testid="profile-logout-btn"
        onClick={async () => { await logout(); onClose(); }}
        className="cy-btn cy-btn-magenta text-xs w-full justify-center"
      >
        <LogOut size={14} className="mr-1" /> LOGOUT
      </button>
    </div>
  );
}

export default function NavHeader() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b-2 border-[color:var(--border-hard)] bg-[color:var(--bg-void)]/95 backdrop-blur">
      <div className="max-w-[1400px] mx-auto pl-3 pr-3 sm:pl-6 sm:pr-6 py-3 flex items-center gap-2 sm:gap-4">
        {/* Brand — shrinks aggressively on mobile so the actions get room */}
        <Link to="/" data-testid="brand-link" className="flex items-center gap-1.5 sm:gap-3 shrink-0 mr-1">
          <span className="font-glitch text-xl sm:text-3xl text-[color:var(--accent-yellow)] leading-none">CY_</span>
          <span className="font-display text-lg sm:text-3xl font-black tracking-tighter uppercase text-[color:var(--text-primary)] leading-none">
            <span className="hidden xs:inline">BORG//SHEET</span>
            <span className="xs:hidden">BORG</span>
          </span>
          <span className="hidden sm:inline-block ml-2"><span className="tape tape-magenta">v1.0</span></span>
        </Link>

        {/* Middle: horizontally slideable strip (Dashboard + Chronicles + Sign-in / Local mode). Overflow is contained here so the profile dropdown never gets clipped. */}
        <nav
          data-testid="nav-actions"
          className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 overflow-x-auto no-scrollbar py-1"
          style={{ WebkitOverflowScrolling: "touch", scrollSnapType: "x proximity" }}
        >
          <span aria-hidden className="flex-1 min-w-0" />
          {user ? (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
                className="cy-btn cy-btn-cyan text-xs sm:text-sm inline-flex shrink-0"
                title="Dashboard"
                style={{ scrollSnapAlign: "end" }}
              >
                <LayoutDashboard size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">DASHBOARD</span>
              </button>
              <button
                onClick={() => navigate("/chronicles")}
                data-testid="nav-chronicles-btn"
                className="cy-btn cy-btn-magenta text-xs sm:text-sm inline-flex shrink-0"
                title="Chronicles"
                style={{ scrollSnapAlign: "end" }}
              >
                <ScrollText size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">CHRONICLES</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="nav-local-btn"
                className="cy-btn cy-btn-cyan text-xs sm:text-sm inline-flex shrink-0"
                title="Local Mode"
                style={{ scrollSnapAlign: "end" }}
              >
                <TerminalSquare size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">LOCAL MODE</span>
              </button>
              <button
                onClick={login}
                data-testid="login-btn"
                className="cy-btn cy-btn-solid-yellow text-xs sm:text-sm shrink-0"
                style={{ scrollSnapAlign: "end" }}
              >
                <LogIn size={14} className="sm:mr-1" />
                <span className="hidden sm:inline">SIGN IN</span>
              </button>
            </>
          )}
        </nav>

        {/* Profile trigger — kept OUTSIDE the scrollable strip so it is always tappable and its dropdown never gets clipped by overflow. */}
        {user && (
          <div className="relative shrink-0">
            <button
              data-testid="profile-trigger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className="cy-btn text-xs sm:text-sm inline-flex items-center gap-2"
              title="Profile"
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-4 h-4 sm:w-5 sm:h-5 object-cover"
                  style={{ imageRendering: "pixelated" }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <User size={14} className="text-[color:var(--accent-cyan)]" />
              )}
              <span className="font-mono uppercase tracking-widest max-w-[80px] md:max-w-[220px] truncate">
                {user.name?.split(" ")[0] || user.email?.split("@")[0]}
              </span>
            </button>
            {menuOpen && <ProfileMenu user={user} onClose={() => setMenuOpen(false)} />}
          </div>
        )}
      </div>
    </header>
  );
}
